import React, { useMemo } from "react";
import * as THREE from "three";
import { TrussPlane } from "../../types/truss";
import { getLoadVisualScale } from "@/utils/gridScale";

interface PerpendicularArrowProps {
  position: THREE.Vector3;
  plane: TrussPlane;
  value: number;
  color?: string;
  scale?: number;
  opacity?: number;
  label?: string;
  nodeRadius?: number;
  scaleFactor?: number; // Scale range for visual sizing
}

/**
 * PerpendicularArrow Component
 * Renders arrow pointing based on value sign:
 * - Positive value: Arrow points in +axis direction (outward)
 * - Negative value: Arrow points in -axis direction (inward)
 *
 * Layout (for positive X):
 *   [NODE] --> [CONE HEAD] ====shaft====
 *
 * Layout (for negative X):
 *   ====shaft==== [CONE HEAD] --> [NODE]
 */
export const PerpendicularArrow: React.FC<PerpendicularArrowProps> = ({
  position,
  plane,
  value,
  color,
  scale = 1,
  opacity = 1,
  nodeRadius = 0.3,
  scaleFactor = 1,
}) => {
  const planeDefaults: Record<TrussPlane, string> = {
    X: "#FF0000", // dx - red
    Y: "#22C55E", // dy - green
    Z: "#2563EB", // dz - blue
  };

  const resolvedColor = color || planeDefaults[plane];

  // Get load visual scale from pre-calculated fixed values
  const visualScale = getLoadVisualScale(scaleFactor);

  // Arrow dimensions - scaled based on model scale range
  const arrowLength = 2.5 * visualScale;
  const coneLength = 0.9 * visualScale;
  const coneRadius = 0.22 * visualScale;
  const shaftRadius = 0.07 * visualScale;

  // Determine direction based on value sign
  const forceDirection = value >= 0 ? -1 : 1; // +1 for positive, -1 for negative

  // Base direction for the axis (always positive)
  const baseDir = useMemo(() => {
    switch (plane) {
      case "X":
        return new THREE.Vector3(1, 0, 0);
      case "Y":
        return new THREE.Vector3(0, 1, 0);
      case "Z":
        return new THREE.Vector3(0, 0, 1);
      default:
        return new THREE.Vector3(0, 0, 1);
    }
  }, [plane]);

  // Actual direction based on value sign
  const outwardDir = useMemo(() => {
    return baseDir.clone().multiplyScalar(forceDirection);
  }, [baseDir, forceDirection]);

  const tipClearance = 0.2;

  // Cone head position
  const headPos = useMemo(() => {
    const coneCenter = nodeRadius + tipClearance + coneLength / 2;
    return outwardDir.clone().multiplyScalar(coneCenter);
  }, [outwardDir, nodeRadius, coneLength]);

  // Shaft position
  const shaftCenter = useMemo(() => {
    const coneBase = nodeRadius + tipClearance + coneLength;
    const shaftCenterDist = coneBase + arrowLength / 2;
    return outwardDir.clone().multiplyScalar(shaftCenterDist);
  }, [outwardDir, nodeRadius, arrowLength, coneLength]);

  // Quaternion to rotate cylinder to outward direction
  const shaftQuat = useMemo(() => {
    const defaultAxis = new THREE.Vector3(0, 1, 0);
    const q = new THREE.Quaternion();
    q.setFromUnitVectors(defaultAxis, outwardDir.clone().normalize());
    return q;
  }, [outwardDir]);

  // Quaternion for cone - tip points toward node
  const coneQuat = useMemo(() => {
    const defaultTip = new THREE.Vector3(0, 1, 0);
    const tipDirection = outwardDir.clone().multiplyScalar(-1);
    const q = new THREE.Quaternion();
    q.setFromUnitVectors(defaultTip, tipDirection.normalize());
    return q;
  }, [outwardDir]);

  return (
    <group position={position} scale={[scale, scale, scale]}>
      {/* Shaft */}
      <mesh position={shaftCenter} quaternion={shaftQuat}>
        <cylinderGeometry args={[shaftRadius, shaftRadius, arrowLength, 12]} />
        <meshStandardMaterial
          color={resolvedColor}
          transparent
          opacity={opacity}
        />
      </mesh>

      {/* Cone head */}
      <mesh position={headPos} quaternion={coneQuat}>
        <coneGeometry args={[coneRadius, coneLength, 12]} />
        <meshStandardMaterial
          color={resolvedColor}
          transparent
          opacity={opacity}
        />
      </mesh>
    </group>
  );
};
